import {
  ProspectCriteriaField,
  ProspectSearchCriteria,
} from "@/app/types/prospect/ProspectSearchCriteria";

export type ProspectMatchedCriteria = Partial<Record<ProspectCriteriaField, string[]>>;

const POSITIVE_WEIGHTS: Partial<Record<ProspectCriteriaField, number>> = {
  [ProspectCriteriaField.MUST_HAVE_SIGNALS]: 3,
  [ProspectCriteriaField.TARGET_ROLES]: 2,
  [ProspectCriteriaField.TARGET_COMPANIES]: 2,
  [ProspectCriteriaField.TARGET_SENIORITIES]: 1.5,
  [ProspectCriteriaField.PAIN_SIGNALS]: 1.5,
  [ProspectCriteriaField.TRIGGER_SIGNALS]: 1.5,
  [ProspectCriteriaField.TARGET_INDUSTRIES]: 1.5,
  [ProspectCriteriaField.PREFERRED_SIGNALS]: 1,
  [ProspectCriteriaField.TRUST_SIGNALS]: 1,
  [ProspectCriteriaField.TARGET_FUNCTIONS]: 1,
  [ProspectCriteriaField.TARGET_BUSINESS_MODELS]: 1,
  [ProspectCriteriaField.TARGET_COMPANY_STAGES]: 1,
  [ProspectCriteriaField.TARGET_COMPANY_SIZES]: 1,
  [ProspectCriteriaField.TARGET_TECHNOLOGIES]: 1,
  [ProspectCriteriaField.TARGET_LOCATIONS]: 1,
  [ProspectCriteriaField.SEARCH_KEYWORDS]: 1,
};

export const PROSPECT_EXCLUDE_FIELDS: ProspectCriteriaField[] = [
  ProspectCriteriaField.EXCLUDE_SIGNALS,
  ProspectCriteriaField.EXCLUDE_KEYWORDS,
  ProspectCriteriaField.EXCLUDE_COMPANIES,
];

// "Any-of" categorical fields: a prospect has a single value (one role, one
// industry, one size...). Ticking several must NOT stack points — the dimension
// is simply satisfied (binary). Signals/keywords are cumulative (coverage).
const BINARY_FIELDS: ProspectCriteriaField[] = [
  ProspectCriteriaField.TARGET_ROLES,
  ProspectCriteriaField.TARGET_SENIORITIES,
  ProspectCriteriaField.TARGET_FUNCTIONS,
  ProspectCriteriaField.TARGET_INDUSTRIES,
  ProspectCriteriaField.TARGET_BUSINESS_MODELS,
  ProspectCriteriaField.TARGET_COMPANY_STAGES,
  ProspectCriteriaField.TARGET_COMPANY_SIZES,
  ProspectCriteriaField.TARGET_TECHNOLOGIES,
  ProspectCriteriaField.TARGET_COMPANIES,
  ProspectCriteriaField.TARGET_LOCATIONS,
];

const EXCLUDE_PENALTY = 20;

// Fields the agent can tick to qualify a prospect, in display order.
export const PROSPECT_MATCH_FIELDS: ProspectCriteriaField[] = [
  ...(Object.keys(POSITIVE_WEIGHTS) as ProspectCriteriaField[]),
  ...PROSPECT_EXCLUDE_FIELDS,
];

export function isExcludeField(field: ProspectCriteriaField): boolean {
  return PROSPECT_EXCLUDE_FIELDS.includes(field);
}

// The criteria reduced to the fields that actually drive the fit score (positive
// weights + exclusions), dropping non-matchable noise (title, searchMode,
// searchQueries, recommendedPlatforms). Fed to the LLM as the exact set of keys
// and values it may use for matchedCriteria.
export function pickMatchableCriteria(
  criteria: ProspectSearchCriteria | null | undefined,
): ProspectMatchedCriteria {
  const result: ProspectMatchedCriteria = {};

  if (!criteria) {
    return result;
  }

  for (const field of PROSPECT_MATCH_FIELDS) {
    const values = criteria[field];
    if (values && values.length > 0) {
      result[field] = values;
    }
  }

  return result;
}

export type FitLevel = "high" | "medium" | "low";

export function fitScoreClass(score: number): FitLevel {
  if (score >= 70) {
    return "high";
  }
  if (score >= 45) {
    return "medium";
  }
  return "low";
}

export function computeFitScore(
  criteria: ProspectSearchCriteria | null | undefined,
  matched: ProspectMatchedCriteria,
): number {
  if (!criteria) {
    return 0;
  }

  let score = 0;
  let maxScore = 0;

  for (const key of Object.keys(POSITIVE_WEIGHTS) as ProspectCriteriaField[]) {
    const total = criteria[key]?.length ?? 0;
    if (total === 0) {
      continue;
    }
    const weight = POSITIVE_WEIGHTS[key] ?? 0;
    maxScore += weight;
    const matchedCount = Math.min(matched[key]?.length ?? 0, total);

    if (BINARY_FIELDS.includes(key)) {
      score += matchedCount >= 1 ? weight : 0;
    } else {
      score += weight * (matchedCount / total);
    }
  }

  let percent = maxScore > 0 ? (score / maxScore) * 100 : 0;

  for (const field of PROSPECT_EXCLUDE_FIELDS) {
    percent -= (matched[field]?.length ?? 0) * EXCLUDE_PENALTY;
  }

  return Math.max(0, Math.min(100, Math.round(percent)));
}
